import { filter, find, forEach, some } from 'lodash';

import type { JobTagDisplay } from './job-tag-icons';

/**
 * A job's tags as they should be DRAWN while toggles are still in flight: the
 * server's tags, minus any the user has just taken off, plus any they have
 * just put on.
 *
 * Pure and separate from `useJobTagToggle` so the overlay rule can be tested
 * without a mutation or a cache.
 *
 * A pending add needs the tag's name and icon to draw, which the job doesn't
 * carry yet — they come from the vendor's tag list. One that isn't in that
 * list is left out until the server answers rather than drawn as a blank.
 *
 * ORDER. Tags draw in the order the job stores them, so where a pending tag
 * is drawn has to be where the server will leave it. Every pending add goes
 * on the end — including one for a tag the server still lists. That case is a
 * tag taken off and put back before either request was answered: the server
 * runs the `$pull` and then the `$addToSet`, which appends it. Left at its old
 * position it would jump to the end once both settled.
 * @param serverTags the job's tags from the server relation.
 * @param pending tagId → the state the user asked for; absent means settled.
 * @param tagsById the vendor's tags, for drawing a pending add.
 * @returns the tags to draw: settled server tags in server order, then
 *   pending adds in the order they were asked for.
 */
export function applyPendingTagToggles<T extends JobTagDisplay>(
  serverTags: readonly JobTagDisplay[],
  pending: Readonly<Record<string, boolean>>,
  tagsById: Readonly<Record<string, T>>,
): JobTagDisplay[] {
  // Only the tags with no toggle in flight keep their server position.
  const shown: JobTagDisplay[] = filter(
    serverTags,
    (tag) => pending[tag._id] === undefined,
  );

  forEach(pending, (selected, tagId) => {
    // The server's copy if it has one — the job carries what it needs to draw
    // its own tags even when the vendor's list hasn't loaded.
    const tag = find(serverTags, { _id: tagId }) ?? tagsById[tagId];
    if (selected && tag && !some(shown, { _id: tagId })) {
      shown.push(tag);
    }
  });

  return shown;
}
